import { useTranslation } from 'react-i18next';
import { ErrorBoundarySuspense } from '@/components/error-boundary-suspense';
import { PURCHASE_ORDER_DOCUMENT_UPLOAD_BADGES } from '@/configs/purchase-order-document-upload-badges';
import { PurchaseOrderDocumentUploadBadge } from '@/features/purchase-order/components/purchase-order-document-upload-badge';
import { GET_PURCHASE_ORDER_TOTALS_KEY } from '@/keys';
import { useGetPurchaseOrder } from '@/services/apis/queries/purchase-order/use-get-purchase-order';
import { PurchaseOrderMainComments } from './purchase-order-main-comments';
import { PurchaseOrderMainDate } from './purchase-order-main-date';
import { PurchaseOrderMainDelivery } from './purchase-order-main-delivery';
import { PurchaseOrderMainProductionDeclaration } from './purchase-order-main-production-declaration';
import { PurchaseOrderMainSupplier } from './purchase-order-main-supplier';
import { PurchaseOrderMainTotals } from './purchase-order-main-totals';
import { PurchaseOrderMainTotalsSkeleton } from './purchase-order-main-totals-skeleton';
import { PurchaseOrderSendButton } from './purchase-order-send-button';

type PurchaseOrderMainContentProps = {
  id: string;
};

export function PurchaseOrderMainContent({
  id,
}: PurchaseOrderMainContentProps) {
  const { t } = useTranslation('purchaseOrder');
  const { data: purchaseOrder } = useGetPurchaseOrder(id);
  const initialRecipientLabels: Record<string, string> = {};
  const initialRecipientEmails = (purchaseOrder.supplier.contacts ?? [])
    .map((contact) => {
      const email = contact.email?.trim();
      const name = contact.name?.trim();
      if (!email) {
        return null;
      }
      if (name) {
        initialRecipientLabels[email] = name;
      }
      return email;
    })
    .filter((email): email is string => Boolean(email));

  const showProductionDeclarationWaitingContext =
    purchaseOrder.isConcernedByProductionDeclaration &&
    purchaseOrder.statusCode === 'SUPPLIER_AGREEMENT_RECEIVED';

  return (
    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
      <div className='flex flex-col gap-3 lg:col-span-2'>
        {showProductionDeclarationWaitingContext ? (
          <p className='text-muted-foreground text-sm'>
            {t('productionDeclarationWaitingForValidationContext')}
          </p>
        ) : null}
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2'>
            {PURCHASE_ORDER_DOCUMENT_UPLOAD_BADGES.map((badgeConfig) => (
              <PurchaseOrderDocumentUploadBadge
                badgeConfig={badgeConfig}
                initialRecipientEmails={initialRecipientEmails}
                initialRecipientLabels={initialRecipientLabels}
                isConcernedByProductionDeclaration={
                  purchaseOrder.isConcernedByProductionDeclaration ?? false
                }
                key={badgeConfig.id}
                productionDeclarationId={
                  purchaseOrder.productionDeclarationId ?? null
                }
                productionDeclarationStatus={
                  purchaseOrder.productionDeclarationStatus ?? null
                }
                productionDeclarationToken={
                  purchaseOrder.productionDeclarationToken ?? null
                }
                purchaseOrderId={id}
                purchaseOrderStatusCode={purchaseOrder.statusCode}
                supplierName={purchaseOrder.supplier.name}
              />
            ))}
          </div>
          <PurchaseOrderSendButton purchaseOrderId={id} />
        </div>
      </div>
      <div className='space-y-4'>
        <PurchaseOrderMainSupplier purchaseOrderId={id} />
        <PurchaseOrderMainDelivery purchaseOrderId={id} />
        <PurchaseOrderMainComments purchaseOrderId={id} />
      </div>
      <div className='space-y-4'>
        {purchaseOrder.isConcernedByProductionDeclaration && (
          <PurchaseOrderMainProductionDeclaration purchaseOrderId={id} />
        )}
        <PurchaseOrderMainDate purchaseOrderId={id} />
        <ErrorBoundarySuspense
          errorFallbackWrapper={
            <div className='flex h-full w-full items-center justify-center p-4' />
          }
          loadingFallback={<PurchaseOrderMainTotalsSkeleton />}
          message={t('Unable to load purchase order totals')}
          queryKey={[...GET_PURCHASE_ORDER_TOTALS_KEY, id]}
        >
          <PurchaseOrderMainTotals purchaseOrderId={id} />
        </ErrorBoundarySuspense>
      </div>
    </div>
  );
}
